import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { convertToNumber } from "@app/utils/functions";
import { useEquipment, useUser } from "@services/servicesGenericsTables";
import { useDetector } from "@services/servicesGenericsTables/useDetector";
import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";

const CountingAlfaTotal = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { detectorsList } = useDetector();
  const { currentCounting } = useAnalysis();
  const filteredProcedures = useFilteredProcedures("CON");

  const lisentSqpTsieWhite = useMemo(
    () => form.watch("sqp_tsie_white"),
    [form]
  );
  const lisentSqpTsieSample = useMemo(
    () => form.watch("sqp_tsie_sample"),
    [form]
  );
  const countingValidValue = useMemo(
    () => form.watch("counting_valid"),
    [form]
  );

  const cpmSample = useMemo(() => form.watch("sample_cpm"), [form]);
  const cpmSampleQuimioluminiscencia = useMemo(
    () => form.watch("cpm_sample_quimioluminiscencia"),
    [form]
  );
  const grossWhiteCpm = useMemo(() => form.watch("gross_white_cpm"), [form]);
  const quimioluminiscenciaWhite = useMemo(
    () => form.watch("cpm_sample_quimioluminiscencia"),
    [form]
  );

  useEffect(() => {
    const sqpTsieWhite = parseFloat(form.getValues("sqp_tsie_white"));
    const sqpTsieSample = parseFloat(form.getValues("sqp_tsie_sample"));

    if (!isNaN(sqpTsieWhite) && !isNaN(sqpTsieSample)) {
      const variation = (sqpTsieSample / sqpTsieWhite) * 100 - 100;
      const formatVariation_sqp_tsie = variation.toFixed(2).replace(",", ".");
      form.setValue("variation_sqp_tsie", formatVariation_sqp_tsie);
      const appliedCriterion = parseFloat(form.getValues("applied_criterion"));
      if (!isNaN(appliedCriterion)) {
        const lowerBound = 1 - appliedCriterion / 100;
        const upperBound = 1 + appliedCriterion / 100;
        const isValid =
          variation >= lowerBound * 100 - 100 &&
          variation <= upperBound * 100 - 100;
        form.setValue("counting_valid", isValid ? "S" : "N");
      } else {
        form.setValue("counting_valid", "");
      }
    } else {
      form.setValue("counting_valid", "");
    }
  }, [lisentSqpTsieWhite, lisentSqpTsieSample]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const totalSampleCpm =
      convertToNumber(cpmSample) -
      convertToNumber(cpmSampleQuimioluminiscencia);
    if (!isNaN(totalSampleCpm)) {
      form.setValue(
        "total_cpm_sample",
        totalSampleCpm.toFixed(2).replace(",", ".")
      );
    }
  }, [cpmSample, cpmSampleQuimioluminiscencia]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const totalWhiteCpm =
      parseFloat(grossWhiteCpm) - parseFloat(quimioluminiscenciaWhite);
    if (!isNaN(totalWhiteCpm)) {
      const formattotalWhiteCpm = totalWhiteCpm.toFixed(2).replace(",", ".");

      form.setValue("total_cpm_white", formattotalWhiteCpm);
    }
  }, [grossWhiteCpm, quimioluminiscenciaWhite]); // eslint-disable-line react-hooks/exhaustive-deps

  const stylesInput = useMemo(() => {
    if (countingValidValue === "N") {
      return { backgroundColor: "#ef233c" };
    } else if (countingValidValue === "S") {
      return { backgroundColor: "#a7c957" };
    }
    return {};
  }, [countingValidValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const watchEquipment = useMemo(
    () => form.watch("counting_equipment"),
    [form.watch("counting_equipment")]
  );

  const filterDetectors = useMemo(() => {
    const idsEquipment = watchEquipment?.map((e) => e.id);
    return detectorsList.filter(
      (detector) => idsEquipment?.includes(detector.equipment.id) || false
    );
  }, [watchEquipment, detectorsList]);

  return (
    <>
      {currentCounting.index !== undefined ? (
        <React.Fragment key={currentCounting.id}>
          <ResponsiveFormContainer columns={5}>
            <MAutocomplete
              name="counting_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
              disabled={!currentCounting.canEdit}
            />

            <MAutocomplete
              name="counting_procedure"
              options={filteredProcedures || []}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              validation={{ required: "El procedimiento es requerido" }}
              disabled
            />
            <MAutocomplete
              name="counting_equipment"
              options={equipmentList || []}
              label="Seleccione un Equipo"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El equipo es requerido" }}
              multiple={true}
              disabled={!currentCounting.canEdit}
            />
            <MAutocomplete
              name="counting_detector"
              options={filterDetectors || []}
              label="Seleccione un detector"
              getOptionLabel={(option) => `${option.name}` || ""}
              multiple={true}
              disabled={!currentCounting.canEdit}
            />
            <MDatePicker
              key="counting_date"
              name="counting_date"
              pickerType="date"
              label="Fecha de contaje"
              validation={{
                required: "La fecha de contaje es requerida",
              }}
              disabled={!currentCounting.canEdit}
            />

            <MTextField
              name="total_sample_counting_time"
              placeholder="Tiempo de contaje (min)"
              label="Tiempo de contaje (min)"
              validation={{ required: "El Tiempo de contaje es requerido" }}
              fullWidth
              type="number"
              step="0.001"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="total_cpm_sample"
              placeholder="cpm Muestra"
              label="cpm Muestra"
              validation={{ required: "El cpm Muestra es requerido" }}
              fullWidth
              type="number"
              step="0.001"
              disabled={!currentCounting.canEdit}
            />
            <MTextField
              name="counting_valid"
              placeholder="Contaje Válido"
              label="Contaje Válido"
              type="text"
              fullWidth
              disabled
              style={stylesInput}
            />
          </ResponsiveFormContainer>
        </React.Fragment>
      ) : null}
    </>
  );
};

export default CountingAlfaTotal;
